<template>
  <!--
    AutomationBadges — renders one mini-badge per unique automation bound to
    this device. Only shown in button mode (dashboard card).
    Place with `absolute` positioning inside a `relative` wrapper.
  -->
  <div v-if="uniqueAutomations.length" class="automation-badges">
    <div
      v-for="automation in uniqueAutomations"
      :key="automation.automationId"
      class="automation-badge"
      :class="[badgeClass(automation), !automation.enabled ? 'automation-badge--disabled' : '']"
      :title="badgeTitle(automation)">
      <!-- Icon -->
      <span class="material-symbols-outlined automation-badge__icon">
        {{ getMeta(automation.automationType).icon }}
      </span>

      <!-- Countdown text (timer automations only, when active) -->
      <span v-if="countdownText(automation)" class="automation-badge__countdown">{{ countdownText(automation) }}</span>

      <!-- Active mode label (multi-mode automations, e.g. sun controller) -->
      <span v-else-if="activeModeLabel(automation)" class="automation-badge__mode">{{
        activeModeLabel(automation)
      }}</span>

      <!-- Active pulse dot (active + no countdown) -->
      <span v-else-if="isActive(automation)" class="automation-badge__dot"></span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, watch } from 'vue';
import { useDeviceAutomationsStore, type DeviceAutomationBinding } from '../../stores/deviceAutomations';
import { useDeviceValues } from '../../composables/useDeviceValues';
import { tt } from '../../utils/consoleText';
import {
  getBadgeMeta,
  getActiveModeLabel,
  prefixedKey,
  useIsoCountdown,
  useSecondsCountdown,
} from '../../composables/useAutomationBadge';

// ─── Props ────────────────────────────────────────────────────────────────────

interface Props {
  deviceId: string;
  /** Pass device.deviceValues so automation state values are available immediately */
  deviceValues?: Array<{ valueKey: string; currentValue?: unknown }>;
  /** Automation ids to skip — already visualized by the widget's own indicator */
  excludeIds?: string[];
  /** Automation types to skip — already visualized by the widget's own indicator */
  excludeTypes?: string[];
}

const props = defineProps<Props>();

// ─── Data ─────────────────────────────────────────────────────────────────────

const store = useDeviceAutomationsStore();
onMounted(() => store.queueDeviceAutomations(props.deviceId));

const { currentValues, initializeValues } = useDeviceValues(props.deviceId);

// Hydrate from initial device values prop (includes automation state values)
if (props.deviceValues?.length) initializeValues(props.deviceValues);
watch(
  () => props.deviceValues,
  (v) => {
    if (v?.length) initializeValues(v);
  },
  { deep: true },
);
watch(
  () => props.deviceId,
  (deviceId) => {
    store.queueDeviceAutomations(deviceId);
  },
);

// All bindings for this device, deduplicated by automationId.
// Only automations this device actually DRIVES get a badge (role output*):
// input roles (trigger / brightness / presence / condition / inhibit …) stay
// silent — e.g. a motion sensor that only feeds a light's motion switch shows
// no badge; that automation is visualized on the device it controls instead.
const uniqueAutomations = computed<DeviceAutomationBinding[]>(() => {
  const seen = new Set<string>();
  const excludedIds = new Set(props.excludeIds ?? []);
  const excludedTypes = new Set(props.excludeTypes ?? []);
  return store.getDeviceAutomations(props.deviceId).filter((b) => {
    if (!b.bindingRole?.startsWith('output')) return false;
    if (excludedIds.has(b.automationId)) return false;
    if (excludedTypes.has(b.automationType)) return false;
    if (seen.has(b.automationId)) return false;
    seen.add(b.automationId);
    return true;
  });
});

// ─── Per-automation helper fns ────────────────────────────────────────────────

function getMeta(type: string) {
  return getBadgeMeta(type);
}

function getStateValue(automation: DeviceAutomationBinding, relativeKey: string | null): unknown {
  if (!relativeKey) return undefined;
  return currentValues.value[prefixedKey(automation.automationType, relativeKey)];
}

function isActive(automation: DeviceAutomationBinding): boolean {
  if (!automation.enabled) return false;
  const meta = getMeta(automation.automationType);
  if (!meta.activeStateKey) return false;
  const v = getStateValue(automation, meta.activeStateKey);
  return v === true || v === 1;
}

// ─── Countdown: create one reactive countdown per unique automation ───────────
// We use a WeakMap-like approach keyed by automationId string.
const isoCountdowns = new Map<string, ReturnType<typeof useIsoCountdown>>();
const secCountdowns = new Map<string, ReturnType<typeof useSecondsCountdown>>();

function ensureCountdowns(automations: DeviceAutomationBinding[]) {
  for (const a of automations) {
    const meta = getMeta(a.automationType);
    if (meta.countdownIsoKey && !isoCountdowns.has(a.automationId)) {
      isoCountdowns.set(
        a.automationId,
        // The badge meta names an ISO-timestamp key, so the state value is one.
        useIsoCountdown(() => getStateValue(a, meta.countdownIsoKey) as string | null | undefined),
      );
    }
    if (meta.countdownSecondsKey && !secCountdowns.has(a.automationId)) {
      secCountdowns.set(
        a.automationId,
        // …and a seconds key, whose value is a number.
        useSecondsCountdown(() => getStateValue(a, meta.countdownSecondsKey) as number | null | undefined),
      );
    }
  }
}

// Ensure countdowns are set up reactively when automations load
const _setup = computed(() => {
  ensureCountdowns(uniqueAutomations.value);
  return null;
});
// Trigger the computed so it stays tracked
void _setup.value;

function countdownText(automation: DeviceAutomationBinding): string {
  if (!automation.enabled) return '';
  if (!isActive(automation)) return '';
  const meta = getMeta(automation.automationType);
  if (meta.countdownIsoKey) {
    return isoCountdowns.get(automation.automationId)?.value ?? '';
  }
  if (meta.countdownSecondsKey) {
    return secCountdowns.get(automation.automationId)?.value ?? '';
  }
  return '';
}

function activeModeLabel(automation: DeviceAutomationBinding): string {
  if (!automation.enabled) return '';
  return getActiveModeLabel(automation.automationType, currentValues.value) ?? '';
}

// ─── Badge styling ────────────────────────────────────────────────────────────

function badgeClass(automation: DeviceAutomationBinding): string {
  if (!automation.enabled) return 'bg-gray-700/60';
  const meta = getMeta(automation.automationType);
  return isActive(automation) ? meta.activeBg : meta.idleBg;
}

function badgeTitle(automation: DeviceAutomationBinding): string {
  const status = !automation.enabled
    ? tt('disabled', 'onemogočeno')
    : isActive(automation)
      ? tt('active', 'aktivno')
      : tt('enabled', 'omogočeno');
  const cd = countdownText(automation);
  const mode = activeModeLabel(automation);
  const parts = [`${automation.automationName} (${status})`];
  if (mode) parts.push(mode);
  if (cd) parts.push(cd);
  return parts.join(' — ');
}
</script>
